import {faCircleInfo} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Space, Table, Tooltip, Typography} from 'antd';
import {ColumnsType} from 'antd/lib/table';

import {Locale} from '../../../../../../provider/locale/locale';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {AiMonitoringRunType} from '../../../../../../service/ai-brand-monitoring/ai-brand-monitoring-type';
import {MetricDelta} from '../../metric-delta';

import * as styles from './overview-block.scss';

type PropsType = {
    run: AiMonitoringRunType;
    brandName?: string;
};

type RankingRowType = {
    key: string;
    name: string;
    score: number;
    delta: number | null;
    isBrand: boolean;
};

function buildRankingRows(run: AiMonitoringRunType, brandName: string): Array<RankingRowType> {
    const rows: Array<RankingRowType> = [
        {
            key: 'brand',
            name: brandName,
            score: run.brandScore ?? 0,
            delta: run.brandScoreDelta,
            isBrand: true,
        },
        ...run.competitors
            .filter((competitor) => competitor.name.trim().length > 0)
            .map((competitor) => ({
                key: `competitor-${competitor.id}`,
                name: competitor.name,
                score: competitor.score,
                delta: competitor.delta,
                isBrand: false,
            })),
    ];

    return rows.sort((first, second) => second.score - first.score);
}

export function OverviewBlock(props: PropsType): JSX.Element {
    const {run, brandName} = props;
    const {getLocalizedString} = useLocale();

    const resolvedBrandName = brandName || getLocalizedString('AI_BRAND_MONITORING__DASHBOARD__YOUR_BRAND');
    const rows = buildRankingRows(run, resolvedBrandName);

    const brandRank = rows.findIndex((row) => row.isBrand) + 1;
    const competitorCount = rows.length - 1;
    const topCompetitor = rows.find((row) => !row.isBrand);

    function renderInsight(): string {
        if (competitorCount === 0) {
            return getLocalizedString('AI_BRAND_MONITORING__OVERVIEW__INSIGHT_NO_COMPETITORS');
        }

        if (brandRank === 1) {
            return getLocalizedString('AI_BRAND_MONITORING__OVERVIEW__INSIGHT_LEADER');
        }

        return getLocalizedString('AI_BRAND_MONITORING__OVERVIEW__INSIGHT', {
            rank: String(brandRank),
            total: String(rows.length),
            competitor: topCompetitor?.name ?? '',
        });
    }

    const columns: ColumnsType<RankingRowType> = [
        {
            title: getLocalizedString('AI_BRAND_MONITORING__TABLE__NAME'),
            dataIndex: 'name',
            key: 'name',
            render: (name: string, row: RankingRowType) =>
                row.isBrand ? <Typography.Text strong>{name}</Typography.Text> : name,
        },
        {
            title: getLocalizedString('AI_BRAND_MONITORING__DASHBOARD__VISIBILITY_SCORE'),
            dataIndex: 'score',
            key: 'score',
            render: (score: number, row: RankingRowType) =>
                row.isBrand ? <Typography.Text strong>{score}</Typography.Text> : score,
        },
        {
            title: (
                <Space size={4}>
                    {getLocalizedString('AI_BRAND_MONITORING__TABLE__DELTA')}
                    <Tooltip title={getLocalizedString('AI_BRAND_MONITORING__TABLE__DELTA_TOOLTIP')}>
                        <FontAwesomeIcon className={styles.overview_block__info_icon} icon={faCircleInfo} />
                    </Tooltip>
                </Space>
            ),
            dataIndex: 'delta',
            key: 'delta',
            render: (delta: number | null) => <MetricDelta delta={delta} emptyLabel="—" />,
        },
    ];

    return (
        <div>
            <Typography.Title level={5}>
                <Locale stringKey="AI_BRAND_MONITORING__OVERVIEW__TITLE" />
            </Typography.Title>
            <Typography.Paragraph>{renderInsight()}</Typography.Paragraph>
            <Table<RankingRowType>
                columns={columns}
                dataSource={rows}
                pagination={false}
                rowClassName={(row) => (row.isBrand ? 'ant-table-row-selected' : '')}
                rowKey="key"
                size="small"
            />
        </div>
    );
}
